/**
 * Plugins › Connections › Browsers: whether pages Denker opens get their
 * cookie banner refused - or hidden when it offers no refusal - and a pop-up
 * over them closed, so Denker sees the page as a person does. Off until the
 * person turns it on; Rust owns the choice (`popups.rs`).
 */
import { useCallback, useEffect, useState } from "react";
import { PocketRow, ToggleSwitch } from "@/components/ui";
import {
  CLOSE_POPUPS_CHANGED_EVENT,
  getClosePopups,
  setClosePopups,
} from "@/desktop/denker-browser";
import { listenWhileMounted } from "@/desktop/tauri-listener-cleanup";
import { useT } from "@/i18n";
import { settingsBrowserMessages } from "@/i18n/messages/settings-browser";
import { SettingsFieldError } from "./settings-text-field";

const message = (failure: unknown) =>
  failure instanceof Error ? failure.message : String(failure);

export function BrowserPopupsRow() {
  const t = useT(settingsBrowserMessages);
  const [close, setClose] = useState<boolean | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    // A change told meanwhile is newer than what the first read finds.
    let told = false;
    void getClosePopups().then(
      (next) => {
        if (current && !told) setClose(next);
      },
      () => {
        if (current) setError(t("popupsReadFailed"));
      },
    );
    // Turned on from the hint while Settings is open.
    const stop = listenWhileMounted<unknown>(
      CLOSE_POPUPS_CHANGED_EVENT,
      ({ payload }) => {
        if (typeof payload !== "boolean") return;
        told = true;
        setClose(payload);
      },
    );
    return () => {
      current = false;
      stop();
    };
  }, [t]);

  const choose = useCallback(
    async (next: boolean) => {
      if (saving) return;
      setSaving(true);
      setError(null);
      try {
        await setClosePopups(next);
        setClose(next);
      } catch (failure) {
        setError(message(failure));
      }
      setSaving(false);
    },
    [saving],
  );

  if (close === null && !error) return null;
  return (
    <PocketRow
      testId="settings-row-browser-close-popups"
      title={t("popupsTitle")}
      secondLine={t("popupsLine")}
      trailing={
        close === null ? null : (
          <ToggleSwitch
            checked={close}
            disabled={saving}
            onCheckedChange={(next) => void choose(next)}
            aria-label={t("popupsTitle")}
            data-testid="browser-close-popups-toggle"
          />
        )
      }
      below={
        error ? (
          <SettingsFieldError
            error={error}
            testId="browser-close-popups-error"
          />
        ) : undefined
      }
    />
  );
}
